import * as React from 'react';
import cx from 'clsx';
import dayjs from 'dayjs';
import { Alert, Button, Icon, Spinner } from '@vechaiui/react';
import { CheckCircleIcon, ShieldCheckIcon } from '@heroicons/react/solid';
import { Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import { useLocalization } from '@fluent/react';
import { useMutation, useQuery } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';
import { useToggle } from '@react-hookz/web';

import Dialog from '#/components/controls/Dialog';
import FileCompoundButton from '#/components/controls/FileCompoundButton';
import FreightDeliveryDetailsPanel from '#/components/panels/FreightDeliveryDetailsPanel';
import FreightDeliveryInfoPanel from '#/components/panels/FreightDeliveryInfoPanel';
import OrderCancellationPanel from './OrderCancellationPanel';
import TransporterInfoForm from '#/components/forms/TransporterInfoForm';
import TripDetailsPanel from '#/components/panels/TripDetailsPanel';
import yup from '#/libs/yup';
import { ApiErrorBlock } from '#/components/forms/ErrorBlock';
import {
  CreateFreightDeliveryInputDocument,
  GetOrdersByOrderNumberDocument,
  OrderDetailsPanelFragmentDoc,
  OrderHeaderInfoFragmentDoc,
  OrderStatus,
  PaymentMethod,
  PaymentStatusType,
  RequestForQuotationStatus,
  RespondRfqDocument,
  RouteLegDetailsFragmentDoc,
  UpdateOrderStatusDocument,
} from '#/__generated__/graphql/graphql';
import { FragmentType, getFragmentData } from '#/__generated__/graphql';
import { FREIGHT_DELIVERY_INITIAL_VALUES } from '#/constants/form-initial-values';
import { SearchParam } from '#/constants/search-params';
import { formatMoney, formatBytes } from '#/utils/formatter';
import { freightDeliverySchema } from '#/constants/validation-schema';
import { sanitizeFormData } from '#/utils/sanitize-data';
import { useManagingProfile } from '#/hooks';

const OrderDetailsPanel: React.FC<OrderDetailsPanelProps> = ({ orderNumber }) => {
  const [, setSearchParams] = useSearchParams();
  const managingProfile = useManagingProfile();
  const { l10n } = useLocalization();

  const [apiErrors, setApiErrors] = useState<{ message: string }[]>();
  const [isCancelOrderDialogOpen, toggleCancelDialog] = useToggle(false);
  const [isCancelOrderFailedDialogOpen, toggleCancelOrderFailedDialog] = useToggle(false);
  const [isReqBiddingDialogOpen, toggleReqBiddingDialog] = useToggle(false);
  const [isOrderDeliveredDialogOpen, toggleOrderDeliveredDialog] = useToggle(false);
  const [reqBidding, toggleReqBidding] = useToggle();
  const [file, setFile] = useState<File>();

  const { data, loading } = useQuery(GetOrdersByOrderNumberDocument, {
    variables: { orderNumber },
  });
  const [createFreightDelivery] = useMutation(CreateFreightDeliveryInputDocument);
  const [respondRfqMutation] = useMutation(RespondRfqDocument);
  const [updateOrderStatusMutation] = useMutation(UpdateOrderStatusDocument);

  const companyContactNumber = import.meta.env.VITE_COMPANY_CONTACT_NUMBER;
  const companyEmailAddress = import.meta.env.VITE_COMPANY_ADMIN_ADDRESS;

  const CUSTOMER_CANCEL_CUTOFF_HOUR = 6;
  const LOGISTIC_CANCEL_CUTOFF_HOUR = 4;
  const cutoffHour =
    managingProfile?.__typename === 'Customer'
      ? CUSTOMER_CANCEL_CUTOFF_HOUR
      : LOGISTIC_CANCEL_CUTOFF_HOUR;

  const handleDocumentUploadInput = (ev: React.ChangeEvent<HTMLInputElement>) => {
    setFile(ev.target.files?.[0]);
  };
  // Request bidding-related handlers
  const handleReqBiddingClick = async () => {
    await respondRfqMutation({
      refetchQueries: [GetOrdersByOrderNumberDocument],
      variables: { input: { bid: reqBidding, id: order.acceptedOffer.id } },
    });
    toggleReqBiddingDialog(false);
  };
  const handleReqBiddingDialogTriggerClick = (accept: boolean) => {
    toggleReqBidding(accept);
    toggleReqBiddingDialog(true);
  };

  const handleCancelOrderClick = async () => {
    if (dayjs().diff(order.acceptedOffer.pickupTime, 'hour') <= cutoffHour) {
      toggleCancelDialog(true);
    } else {
      toggleCancelOrderFailedDialog(true);
    }
  };

  // Order delivered-related handlers
  const handleOrderDeliveredClick = async () => {
    try {
      await updateOrderStatusMutation({
        refetchQueries: [GetOrdersByOrderNumberDocument],
        variables: {
          input: {
            id: order.id,
            orderStatus: OrderStatus.OrderDelivered,
            ...(!!file ? { attachments: [{ contentFile: file }] } : {}),
          },
        },
      });
      alert(l10n.getString('trip-complete', { br: '\n', orderNumber }));
    } catch (err: any) {
      if (err.errors) {
        setApiErrors(err.errors);
        alert(err.errors);
      } else if (err.networkError?.result.errors) {
        setApiErrors(err.networkError.result.errors);
        alert(err.networkError?.result.errors);
      } else {
        setApiErrors([err]);
        alert([err]);
      }
    } finally {
      toggleOrderDeliveredDialog(false);
    }
  };
  const handlePaymentButtonPressed = () => {
    if (payment.paymentUrl) {
      window.location.href = payment.paymentUrl;
    } else {
      console.error('No payment URL');
    }
  };

  useEffect(() => {
    if (!loading && !data?.orders?.edges[0]?.node) {
      setSearchParams((sp) => {
        sp.delete(SearchParam.OrderNumber);
        return sp;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  if (loading || !data?.orders?.edges[0]?.node) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner size="xl" />
      </div>
    );
  }

  const order = getFragmentData(OrderDetailsPanelFragmentDoc, data.orders.edges[0].node);
  const payment = order.payments[0];
  const isCreditTerm = !!payment && payment.paymentMethod === PaymentMethod.CreditTerms;
  const preferredVehicleBodyType = order.acceptedOffer.preferredVehicle?.bodyType;
  const isRequestConfirmed = order.orderDeliveries.length > 0;

  const CancelOrderButton = () => {
    return (
      <Button
        className="w-1/2 max-w-2xl rounded-full"
        color="red"
        size="xl"
        variant="solid"
        onClick={() => handleCancelOrderClick()}
      >
        {l10n.getString('cancel')} {l10n.getString('trip')}
      </Button>
    );
  };

  return (
    <div className="bg-white">
      <div className="relative z-0 p-8 2xl:p-16">
        {/*
        <NavigationalBarPanel
          // menuItems={[
          //   {
          //     onClick: handleEditOrder,
          //     title: l10n.getString('edit-order'),
          //   },
          //   {
          //     onClick: handleReportOrder,
          //     title: l10n.getString('report-order'),
          //   },
          // ]}
          tabs={[
            {
              anchorTag: Anchor.OrderDetails,
              title: l10n.getString('order-details'),
            },
            {
              anchorTag: Anchor.TransporterInfo,
              title: l10n.getString('transport-info'),
            },
            // {
            //   anchorTag: Anchor.TripActivity,
            //   title: l10n.getString('trip-activity'),
            // },
            // {
            //   anchorTag: Anchor.Documents,
            //   title: l10n.getString('documents'),
            // },
          ]}
        />
        */}

        <OrderHeaderInfo order={order} />

        {managingProfile?.__typename === 'Customer' && isCreditTerm && (
          <div className="my-4">
            <Alert variant="subtle" color="amber" className="text-amber-700">
              {l10n.getString('credit-term-message')}
              <span className="ml-1 font-bold">
                {dayjs(order.orderDate)
                  .add(payment.creditPeriod?.value ?? 0, 'day')
                  .format('DD/MM/YYYY')}
              </span>
              .
            </Alert>
          </div>
        )}

        <div className="mt-4 space-y-12">
          <div>
            <FreightDeliveryDetailsPanel
              companyName={order.seller?.name ?? ''}
              freightDelivery={order.orderDeliveries?.[0]}
              status={order.orderStatus}
            />
          </div>
          <div>
            <TripDetailsPanel order={order} />
          </div>
          {order.orderDeliveries.length > 0 && (
            <div className="mb-5 border-t">
              <div className="my-8 text-3xl font-semibold">
                {l10n.getString('transporter')} {l10n.getString('info')}
              </div>
              <div className="pb-10">
                <FreightDeliveryInfoPanel
                  attachments={order.attachments || undefined}
                  companyName={order.seller?.name ?? ''}
                  freightDelivery={order.orderDeliveries[0]}
                />
              </div>
            </div>
          )}
          {managingProfile?.__typename === 'Customer' &&
            (order.orderStatus === OrderStatus.OrderProcessing ||
              order.orderStatus === OrderStatus.OrderPending) && (
              <div className="flex justify-center">
                <CancelOrderButton />
              </div>
            )}
          {/*
          <div>
            {!!activityDetails && (
              <div className="px-20 pt-10 pb-5 mb-5 bg-gray-100">
                <TripActivityTimeline activityDetails={activityDetails} />
              </div>
            )}
            {(!!deliveryOrder || !!invoice) && (
              <div className="px-20 py-10">
                <TripActionsPanel
                  deliveryOrder={{
                    id: '2',
                    documentUrl: '',
                  }}
                />
              </div>
            )}
          </div>
          */}
        </div>
        {managingProfile?.__typename === 'Customer' &&
          order.orderStatus === OrderStatus.OrderPaymentDue &&
          !!payment &&
          payment.paymentMethod !== PaymentMethod.CreditTerms && (
            <div className="py-8 mt-12 border-t">
              <Button
                className="w-full rounded-full"
                color="primary"
                onClick={handlePaymentButtonPressed}
                size="xl"
                variant="solid"
              >
                {l10n.getString('make-payment')}
              </Button>
            </div>
          )}

        {managingProfile?.__typename === 'Logistics' && (
          <div className="py-8 mt-12 border-t">
            {/* Step 1: Accept/Reject request */}
            {((order.orderStatus !== OrderStatus.OrderPaymentDue &&
              order.orderStatus !== OrderStatus.OrderCancelled) ||
              (order.orderStatus === OrderStatus.OrderPaymentDue && isCreditTerm)) &&
              order.acceptedOffer.requestStatus === RequestForQuotationStatus.OpenForBidding && (
                <div className="grid grid-cols-2 gap-4">
                  <Button
                    className="rounded-full"
                    color="red"
                    onClick={() => handleReqBiddingDialogTriggerClick(false)}
                    size="xl"
                    variant="solid"
                  >
                    {`${l10n.getString('reject')} ${l10n.getString('trip')}`}
                  </Button>
                  <Button
                    className="rounded-full"
                    color="primary"
                    onClick={() => handleReqBiddingDialogTriggerClick(true)}
                    size="xl"
                    variant="solid"
                  >
                    {`${l10n.getString('accept')} ${l10n.getString('trip')}`}
                  </Button>
                </div>
              )}

            {/* Step 2: Confirm order (aka add freight delivery info) */}
            {order.acceptedOffer.requestStatus !== RequestForQuotationStatus.OpenForBidding &&
              !isRequestConfirmed && (
                <Formik
                  initialValues={FREIGHT_DELIVERY_INITIAL_VALUES}
                  onSubmit={async (formData, { resetForm }) => {
                    const firstLeg = getFragmentData(
                      RouteLegDetailsFragmentDoc,
                      order.acceptedOffer.routeLegs[0],
                    );
                    const deliveryAddress = { ...firstLeg.endLocation.address };
                    const originAddress = { ...firstLeg.startLocation.address };

                    const form = sanitizeFormData({
                      ...formData,
                      deliveryAddress,
                      originAddress,
                      partOfOrderId: order.id,
                    });
                    try {
                      const response = await createFreightDelivery({
                        refetchQueries: [GetOrdersByOrderNumberDocument],
                        variables: { input: form },
                      });
                      resetForm();
                      if (!!response.data?.createFreightDelivery?.node.id) {
                        alert('Congratulation, the trip is a GO!'); // Make this a modal
                      }
                      window.location.reload();
                    } catch (err: any) {
                      if (err.errors) {
                        setApiErrors(err.errors);
                      } else if (err.networkError?.result.errors) {
                        setApiErrors(err.networkError.result.errors);
                      } else {
                        setApiErrors([err]);
                      }
                    }
                  }}
                  validationSchema={yup.object(freightDeliverySchema)}
                >
                  {() => (
                    <Form>
                      <header className="mb-8">
                        <h4>
                          {l10n.getString('transporter')} {l10n.getString('info')}
                        </h4>
                      </header>

                      <div>
                        <TransporterInfoForm preferredVehicleBodyType={preferredVehicleBodyType} />
                      </div>

                      <footer className="mt-12 space-y-4 flex justify-center">
                        {apiErrors && <ApiErrorBlock errors={apiErrors} />}
                        <Button
                          className="w-1/2 max-w-2xl rounded-full"
                          color="primary"
                          size="xl"
                          type="submit"
                          variant="solid"
                        >
                          {l10n.getString('confirm')} {l10n.getString('trip')}
                        </Button>
                      </footer>
                    </Form>
                  )}
                </Formik>
              )}

            {/* Step 3: Mark order as delivered */}
            <footer>
              {order.orderStatus === OrderStatus.OrderProcessing && isRequestConfirmed && (
                <div className="space-y-6">
                  <div>
                    <FileCompoundButton
                      className={cx(!!file && 'bg-slate-200')}
                      onChange={handleDocumentUploadInput}
                    />
                    {!!file && (
                      <div className="my-5">
                        {l10n.getString('uploaded')}: {file.name} {formatBytes(file.size, 0)}
                      </div>
                    )}
                  </div>
                  <div className="flex justify-center">
                    <Button
                      className="w-1/2 max-w-2xl rounded-full"
                      color="primary"
                      size="xl"
                      variant="solid"
                      onClick={() => toggleOrderDeliveredDialog(true)}
                    >
                      {l10n.getString('complete')} {l10n.getString('trip')}
                    </Button>
                  </div>
                </div>
              )}
              {order.orderStatus === OrderStatus.OrderProcessing && (
                <div className="flex justify-center mt-5">
                  <CancelOrderButton />
                </div>
              )}
            </footer>
          </div>
        )}
      </div>

      <Dialog
        isOpen={isReqBiddingDialogOpen}
        onClose={() => toggleReqBiddingDialog(false)}
        onConfirm={handleReqBiddingClick}
        title={`${l10n.getString(reqBidding ? 'accept' : 'reject')} ${l10n.getString('trip')}?`}
      >
        {l10n.getString(reqBidding ? 'confirm-accept-trip' : 'confirm-reject-trip')}
      </Dialog>

      <Dialog
        isOpen={isOrderDeliveredDialogOpen}
        onClose={() => toggleOrderDeliveredDialog(false)}
        onConfirm={() => handleOrderDeliveredClick()}
        title={`${l10n.getString('complete')} ${l10n.getString('trip')}?`}
      >
        {l10n.getString('confirm-complete-trip')}
      </Dialog>

      <Dialog
        isModal
        isOpen={isCancelOrderDialogOpen}
        onClose={() => toggleCancelDialog(false)}
        title={`${l10n.getString('cancel-order')}?`}
      >
        <OrderCancellationPanel orderId={order.id} orderNumber={orderNumber} />
      </Dialog>

      <Dialog
        isOpen={isCancelOrderFailedDialogOpen}
        closeButton={l10n.getString('close')}
        onClose={() => toggleCancelOrderFailedDialog(false)}
        title={`${l10n.getString('cancel-order')} #${orderNumber}`}
      >
        <div className="py-3 space-y-3 border-t border-black">
          <p className="pb-5">{l10n.getString('cancel-order-not-allowed')}</p>
          <p className="flex">
            <div className="font-semibold w-36">
              <span>{l10n.getString('telephone')}</span>
            </div>
            <span>: {companyContactNumber}</span>
          </p>
          <p className="flex">
            <div className="font-semibold w-36">
              <span>{l10n.getString('email')}</span>
            </div>
            <span>: {companyEmailAddress}</span>
          </p>
        </div>
      </Dialog>
    </div>
  );
};
export type OrderDetailsPanelProps = {
  orderNumber: string;
};

const OrderHeaderInfo: React.FC<OrderHeaderInfoProps> = ({ order: _order }) => {
  const { l10n } = useLocalization();
  const order = getFragmentData(OrderHeaderInfoFragmentDoc, _order);

  const isPaid = order.payments.some((p) => p.paymentStatus === PaymentStatusType.PaymentCompleted);

  return (
    <header className="flex items-center justify-between">
      <div className="text-3xl">
        {l10n.getString('order-number')}
        {order.orderNumber}
      </div>
      <div className="flex flex-col">
        <div className="text-xs text-neutral-400">{l10n.getString('fee-total')}</div>
        <div className="flex items-center space-x-1">
          <div className="text-2xl">{formatMoney(order.orderTotal)}</div>
          {isPaid && (
            <div>
              <Icon as={CheckCircleIcon} label="check-circle" className="w-6 h-6 text-green-500" />
            </div>
          )}
          {order.acceptedOffer.insuranceIncluded && (
            <div>
              <Icon
                as={ShieldCheckIcon}
                label="shield-check"
                className="w-6 h-6 text-primary-500"
              />
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

type OrderHeaderInfoProps = {
  order: FragmentType<typeof OrderHeaderInfoFragmentDoc>;
};

export default OrderDetailsPanel;
